import { createTheme } from '@mui/material/styles'
import * as t from './tokens'
import { buttonAppearance, buttonBase } from './buttonStyles'

const hover = '@media (hover: hover) and (pointer: fine)'
export const disabledButton = {
  background: '#e2e9ef',
  color: '#728594',
  borderColor: '#d5dfe6',
  boxShadow: '0 2px 0 #cbd6df',
  '& .MuiTypography-root, & .MuiSvgIcon-root': { color: 'inherit' },
}
const heading = {
  fontFamily: t.headingFont,
  fontWeight: 800,
  letterSpacing: '-0.015em',
  textShadow: 'none',
  color: t.mainColorDarkText,
}
const displayHeading = { ...heading, fontFamily: t.displayFont, fontWeight: 600 }
export const primaryButton = buttonAppearance('contained', 'primary')

export const appTheme = createTheme({
  palette: {
    mode: 'light',
    primary: {
      main: t.mainColorDarker,
      light: t.mainColorLighter,
      dark: '#096586',
      contrastText: '#ffffff',
    },
    secondary: {
      main: t.altColorDarker,
      light: t.altColorLighter,
      dark: t.altColorDarkText,
      contrastText: '#ffffff',
    },
    text: { primary: t.mainColorDarkText, secondary: t.helperTextColor },
    background: { default: t.canvasColor, paper: '#ffffff' },
    divider: t.borderColor,
    success: { main: '#21846e', light: '#def6ed' },
    warning: { main: '#bc631e', light: t.altColorLighter },
    error: { main: '#cc4c58', light: '#fff0f1' },
    info: { main: t.mainColorDarker, light: t.mainColorLighter },
  },
  shape: { borderRadius: 16 },
  typography: {
    fontFamily: t.bodyFont,
    h1: { ...displayHeading, fontSize: '3rem', lineHeight: 1.08 },
    h2: { ...displayHeading, fontSize: '2.5rem', lineHeight: 1.12 },
    h3: { ...displayHeading, fontSize: '2rem', lineHeight: 1.15 },
    h4: { ...displayHeading, fontSize: '1.75rem' },
    h5: { ...heading, fontSize: '1.5rem' },
    h6: { ...heading, fontSize: '1.2rem' },
    body1: { fontSize: '1rem', lineHeight: 1.6 },
    body2: { fontSize: '.875rem', lineHeight: 1.5 },
    subtitle1: { fontWeight: 700 },
    subtitle2: { fontWeight: 700 },
    button: { textTransform: 'none', fontWeight: 700 },
    caption: { lineHeight: 1.5 },
  },
  components: {
    MuiButtonBase: {
      defaultProps: { disableRipple: true },
      styleOverrides: {
        root: {
          transition:
            'background-color 180ms ease, color 180ms ease, border-color 180ms ease, box-shadow 180ms ease, transform 180ms ease',
          '&.Mui-focusVisible': {
            outline: `3px solid ${t.mainColorDarker}`,
            outlineOffset: 3,
          },
        },
      },
    },
    MuiButton: {
      defaultProps: { disableElevation: true },
      styleOverrides: {
        root: {
          ...buttonBase,
          transition:
            'background-color 180ms ease, color 180ms ease, border-color 180ms ease, box-shadow 180ms ease, transform 180ms ease',
        },
        contained: ({ ownerState }) => buttonAppearance('contained', ownerState.color),
        containedPrimary: primaryButton,
        containedSecondary: buttonAppearance('contained', 'secondary'),
        outlined: ({ ownerState }) => buttonAppearance('outlined', ownerState.color),
        text: ({ ownerState }) => buttonAppearance('text', ownerState.color),
      },
    },
    MuiIconButton: {
      styleOverrides: {
        root: {
          color: t.helperTextColor,
          borderRadius: 14,
          transition: 'background-color 180ms ease, color 180ms ease',
          '&:hover': { backgroundColor: 'transparent' },
          [hover]: { '&:hover': { backgroundColor: t.mainColorLighter } },
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: 14,
          fontWeight: 700,
          height: 32,
          maxWidth: '100%',
          transition:
            'background-color 180ms ease, color 180ms ease, border-color 180ms ease, box-shadow 180ms ease',
          borderColor: t.borderColor,
          backgroundColor: '#edf4f9',
          color: t.mainColorDarkText,
          '&.Mui-disabled': { opacity: 1, ...disabledButton },
        },
        label: {
          paddingLeft: 10,
          paddingRight: 10,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
        },
        clickable: {
          '&:hover': { backgroundColor: '#edf4f9' },
          [hover]: { '&:hover': { backgroundColor: t.mainColorLighter } },
        },
        colorPrimary: {
          backgroundColor: t.mainColorLighter,
          color: t.mainColorMediumText,
        },
        colorSecondary: {
          backgroundColor: t.altColorLighter,
          color: t.altColorDarkText,
        },
        deleteIcon: { color: 'inherit', opacity: 0.7 },
      },
    },
    MuiTabs: {
      styleOverrides: {
        root: {
          background: '#e7eff6',
          borderRadius: 22,
          padding: 6,
          minHeight: 54,
        },
        indicator: { display: 'none' },
        flexContainer: { gap: 4 },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: {
          borderRadius: 17,
          minHeight: 42,
          minWidth: 64,
          padding: '10px 14px',
          textTransform: 'none',
          fontWeight: 700,
          fontSize: 14,
          color: t.helperTextColor,
          '&.Mui-selected': {
            color: '#ffffff',
            background: t.navy,
            boxShadow: '0 2px 0 #adc3d4',
          },
        },
      },
    },
    MuiToggleButton: {
      styleOverrides: {
        root: {
          borderRadius: 16,
          fontWeight: 700,
          textTransform: 'none',
          borderColor: t.borderColor,
          '&.Mui-selected': {
            backgroundColor: t.mainColorLighter,
            color: t.mainColorMediumText,
          },
          '&:hover': { backgroundColor: 'transparent' },
          [hover]: { '&:hover': { backgroundColor: '#edf4f9' } },
        },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          backgroundColor: '#f7fbfe',
          borderRadius: 16,
          color: t.mainColorDarkText,
          '&:hover .MuiOutlinedInput-notchedOutline': {
            borderColor: t.mainColorDarker,
          },
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: t.mainColorDarker,
            borderWidth: 2,
          },
          '&.Mui-disabled': { backgroundColor: '#f0f4f7' },
        },
        notchedOutline: { borderColor: t.borderColor },
        input: { paddingTop: 14, paddingBottom: 14 },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root:not(.Mui-disabled)': {
            background: '#f7fbfe',
          },
        },
      },
    },
    MuiInputLabel: {
      styleOverrides: { root: { color: t.helperTextColor, fontSize: 14 } },
    },
    MuiFormHelperText: {
      styleOverrides: { root: { marginLeft: 4, marginRight: 4 } },
    },
    MuiAutocomplete: {
      styleOverrides: {
        paper: {
          borderRadius: 18,
          border: `1px solid ${t.borderColor}`,
          boxShadow: '0 12px 30px #183d561a',
        },
        tag: { margin: 3, maxWidth: 'calc(100% - 12px)' },
        option: { fontSize: 14 },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: { backgroundImage: 'none' },
        rounded: { borderRadius: 22 },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: t.surfaceRadius,
          border: `1px solid ${t.borderColor}`,
          boxShadow: t.shadow1,
        },
      },
    },
    MuiDialog: {
      styleOverrides: {
        paper: {
          borderRadius: 30,
          padding: 4,
          boxShadow: '0 22px 65px #12355035, 0 3px 0 #bdd2e2',
          margin: 16,
          width: 'calc(100% - 32px)',
          maxHeight: 'calc(100% - 32px)',
        },
      },
    },
    MuiDialogTitle: {
      styleOverrides: {
        root: {
          fontFamily: t.headingFont,
          fontWeight: 800,
          fontSize: 24,
          padding: '20px 22px 12px',
        },
      },
    },
    MuiDialogContent: {
      styleOverrides: {
        root: { padding: '12px 22px 20px', textAlign: 'left' },
      },
    },
    MuiDialogActions: {
      styleOverrides: { root: { padding: '12px 22px 20px', gap: 8 } },
    },
    MuiMenu: {
      styleOverrides: {
        paper: {
          border: `1px solid ${t.borderColor}`,
          borderRadius: 20,
          boxShadow: '0 12px 35px #193b5820',
        },
      },
    },
    MuiMenuItem: {
      styleOverrides: {
        root: { margin: '3px 6px', borderRadius: 12, minHeight: 40 },
      },
    },
    MuiTableContainer: {
      styleOverrides: {
        root: { borderRadius: 18, border: `1px solid ${t.borderColor}` },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          borderBottom: '1px solid #e6eef5',
          padding: '12px 14px',
          fontSize: 14,
        },
        head: {
          background: '#edf4f9',
          color: t.helperTextColor,
          fontWeight: 700,
          fontSize: 12,
          whiteSpace: 'nowrap',
        },
      },
    },
    MuiSkeleton: {
      styleOverrides: {
        root: { backgroundColor: '#e2edf5', borderRadius: 12 },
      },
    },
    MuiLinearProgress: {
      styleOverrides: {
        root: { height: 9, borderRadius: 99, background: '#e2edf5' },
        bar: { borderRadius: 99 },
      },
    },
    MuiAlert: {
      styleOverrides: {
        root: {
          borderRadius: 18,
          fontWeight: 700,
          alignItems: 'center',
          boxShadow: t.shadow2,
        },
        message: { padding: '8px 0' },
      },
    },
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          backgroundColor: t.navy,
          borderRadius: 10,
          padding: '8px 12px',
          fontSize: 12,
        },
      },
    },
  },
})
